import { useCallback, useMemo } from "react";
import { createUseStyles } from "$utils/createUseStyles";
import FileSaver from "file-saver";
import JSZip from "jszip";
import clsx from "clsx";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import Upload2LineIcon from "remixicon-react/Upload2LineIcon";
import { theme } from "$utils/theme/bataillog";
import useAuth from "$hooks/useAuth";
import useJStyles from "$hooks/useJStyles";

import {
  ShippingRecord,
  ShippingFileType,
  Media,
  ShippingFileFormat,
} from "$types";

import InfoContainer from "$components/InfoContainer";
import Text from "$components/Text";
import FileUploader from "$components/FileUploader/FileUploader";
import Button from "$components/Button";

type Props = {
  classes: any;
  shippingRecord: ShippingRecord;
  updateShippingRecord: (shippingRecord: Partial<ShippingRecord>) => void;
  fetchShippingRecord: () => void;
  disabled?: boolean;
};

function ShippingRecordFileUploadBlock({
  shippingRecord,
  classes,
  updateShippingRecord,
  fetchShippingRecord,
  disabled = false,
}: Props) {
  const { isAdmin } = useAuth();
  const localClasses = useStyles();
  const { jclasses } = useJStyles();

  const handleFilesChange = async (files: Media[]) => {
    await updateShippingRecord({
      files: files.map((file: Media) => ({
        name: file.name,
        type: ShippingFileType.OTHER,
        url: file.url,
        mainApiMediaId: file._id,
      })),
    });
    fetchShippingRecord();
  };

  const downloadFile = (file: any, defaultName?: string) =>
    FileSaver.saveAs(file.url, defaultName || "document.pdf");

  const downloadZip = async (files: any[], defaultName: string) => {
    const zip = new JSZip();
    await Promise.all(
      files.map(async (file, index) => {
        const blob = await fetch(file.url).then((r) => r.blob());
        zip.file(
          file.name ||
            `etiquette-${index}.${file.format?.toLowerCase() || "pdf"}`,
          blob
        );
      })
    );
    zip.generateAsync({ type: "blob" }).then((content) => {
      FileSaver.saveAs(content, defaultName || "documents.zip");
    });
  };

  const labelFilesByFormat = useMemo(
    () =>
      (
        shippingRecord?.shipments?.flatMap((shipment) => shipment?.labels) || []
      ).reduce(
        (acc, labelFile) => {
          if (labelFile.format?.toLowerCase() === "pdf")
            acc[ShippingFileFormat.PDF].push(labelFile);
          if (labelFile.format?.toLowerCase() === "zpl")
            // ZPL format was just removed but might come back :)
            acc[ShippingFileFormat.ZPL].push(labelFile);
          return acc;
        },
        { [ShippingFileFormat.PDF]: [], [ShippingFileFormat.ZPL]: [] }
      ),
    [shippingRecord]
  );
  const hasLabelFiles = useMemo(
    () =>
      Object.keys(labelFilesByFormat).some(
        (key) => labelFilesByFormat[key].length > 0
      ),
    [labelFilesByFormat]
  );
  const manifestFiles = useMemo(
    () =>
      shippingRecord?.shipments?.flatMap((shipment) => shipment?.manifests) ||
      [],
    [shippingRecord]
  );

  const handleLabelsDownload = useCallback(
    (format?: ShippingFileFormat) => {
      const labelFiles = labelFilesByFormat[format || ShippingFileFormat.PDF];
      if (!labelFiles || labelFiles.length === 0) return;
      if (labelFiles.length === 1) {
        downloadFile(labelFiles[0], "etiquette.pdf");
        return;
      }
      downloadZip(labelFiles, "etiquettes.zip");
    },
    [labelFilesByFormat]
  );

  const handleManifestsDownload = useCallback(() => {
    if (!manifestFiles || manifestFiles.length === 0) return;
    if (manifestFiles.length === 1) {
      downloadFile(manifestFiles[0], "bordereau.pdf");
      return;
    }
    downloadZip(manifestFiles, "bordereaux.zip");
  }, [manifestFiles]);

  return (
    <InfoContainer
      className={classes.paperContainer}
      bodyClassName={localClasses.mainContainer}
      leftHeader={
        <div className={classes.infoHeader}>
          <AttachFileIcon />
          <Text type="h3">Documents</Text>
        </div>
      }
    >
      <>
        {shippingRecord?.carrierServiceId && (
          <div className={classes.lineContainer}>
            <Button
              onClick={() => handleLabelsDownload(ShippingFileFormat.PDF)}
              variant="contained"
              disabled={!hasLabelFiles}
              className={jclasses.jBtnOnPopper}
            >
              <Upload2LineIcon />
              Etiquettes transporteur
            </Button>
            <div className={localClasses.manifestFileContainer}>
              <Button
                onClick={handleManifestsDownload}
                variant="contained"
                disabled={!manifestFiles}
                className={clsx(
                  jclasses.jBtnOnPopper,
                  localClasses.manifestFileContainer
                )}
              >
                <Upload2LineIcon />
                {`Bordereaux d'enlèvement`}
              </Button>
            </div>
          </div>
        )}
        <div className={classes.lineContainer}>
          <FileUploader
            value={shippingRecord?.media || []}
            onChange={handleFilesChange}
            type="shipping"
            isLargeVersion
            isAdmin={!disabled && isAdmin()}
            isReadOnly={disabled}
            orientation="vertical"
          />
        </div>
      </>
    </InfoContainer>
  );
}

export default ShippingRecordFileUploadBlock;

const useStyles = createUseStyles(() => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    flexGrow: 1,
    maxHeight: 375,
  },
  manifestFileContainer: {
    marginLeft: theme.spacing(2),
  },
}));
